import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  getIpWhitelist,
  createIpWhitelist,
  updateIpWhitelist,
  deleteIpWhitelist,
} from "../../api/ipWhitelistApi";
import IpWhitelistModal from "../../components/pages/IpWhitelistPage/IpWhitelistModal";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { useAuth } from "../../context/AuthContext";
import "./style.css";

const MySwal = withReactContent(Swal);

const IpWhitelistPage = () => {
  const [allIps, setAllIps] = useState([]); // Store all IPs from API
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalState, setModalState] = useState({ isOpen: false, ipData: null });
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10; // Or make this configurable
  const { user } = useAuth();

  const fetchIps = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getIpWhitelist();
      setAllIps(response.data?.items || []);
    } catch (err) {
      console.error("Fetch IPs error:", err);
      if (err.response?.status !== 403) {
        setError("Không thể tải danh sách IP. Vui lòng thử lại.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchIps();
  }, [fetchIps]);

  // Memoize filtered and paginated IPs
  const paginatedIps = useMemo(() => {
    const filteredIps = allIps.filter((ip) =>
      ip.ip.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const startIndex = (currentPage - 1) * pageSize;
    return filteredIps.slice(startIndex, startIndex + pageSize);
  }, [allIps, searchTerm, currentPage, pageSize]);

  const totalPages = useMemo(() => {
    const filteredIps = allIps.filter((ip) =>
      ip.ip.toLowerCase().includes(searchTerm.toLowerCase())
    );
    return Math.ceil(filteredIps.length / pageSize);
  }, [allIps, searchTerm, pageSize]);

  // Reset to page 1 when searching
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handleSave = async (payload, id) => {
    try {
      if (id) {
        // Update
        await updateIpWhitelist(id, payload);
      } else {
        // Create - Add the logged-in user's username to the payload
        const payloadWithCreator = { ...payload, createdBy: user.username };
        await createIpWhitelist(payloadWithCreator);
      }
      MySwal.fire(
        "Thành công!",
        `Đã ${id ? "cập nhật" : "thêm mới"} IP.`,
        "success"
      );
      fetchIps(); // Refresh list after saving
    } catch (err) {
      console.error("Save IP error:", err);
      if (err.response?.status !== 403) {
        MySwal.fire("Lỗi!", "Lưu IP thất bại.", "error");
      }
    }
  };

  const handleDelete = (ip) => {
    MySwal.fire({
      title: "Bạn có chắc không?",
      text: `IP "${ip.ip}" sẽ bị xóa vĩnh viễn!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Đồng ý",
      cancelButtonText: "Hủy",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteIpWhitelist(ip._id);
          MySwal.fire("Đã xóa!", "IP đã được xóa.", "success");
          fetchIps();
        } catch (err) {
          console.error("Delete IP error:", err);
          if (err.response?.status !== 403) {
            MySwal.fire("Lỗi!", "Xóa IP thất bại.", "error");
          }
        }
      }
    });
  };

  return (
    <>
      <div className="users-header">
        <div className="header-content">
          <h3>Quản lý IP</h3>
        </div>
        <div className="header-actions">
          <button
            className="add-user-button"
            onClick={() => setModalState({ isOpen: true, ipData: null })}
          >
            Thêm mới
          </button>
        </div>
      </div>

      <div className="table-controls">
        <input
          type="text"
          placeholder="Tìm kiếm theo IP..."
          className="search-input"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Ghi chú</th>
              <th>Người tạo</th>
              <th>Hành động</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="4" className="table-message">
                  Đang tải...
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan="4" className="table-message error-message">
                  {error}
                </td>
              </tr>
            ) : paginatedIps.length === 0 ? (
              <tr>
                <td colSpan="4" className="table-message">
                  Không tìm thấy IP nào.
                </td>
              </tr>
            ) : (
              paginatedIps.map((ip) => (
                <tr key={ip._id}>
                  <td>
                    <strong>{ip.ip}</strong>
                  </td>
                  <td>{ip.note}</td>
                  <td>{ip.createdBy}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="action-btn edit"
                        title="Chỉnh sửa"
                        onClick={() =>
                          setModalState({ isOpen: true, ipData: ip })
                        }
                      >
                        ✏️
                      </button>
                      <button
                        className="action-btn delete"
                        title="Xóa"
                        onClick={() => handleDelete(ip)}
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="pagination-container">
        <div className="pagination-summary">
          Trang <strong>{currentPage}</strong> / <strong>{totalPages}</strong>
        </div>
        <div className="pagination-controls">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage <= 1}
          >
            Trước
          </button>
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage >= totalPages}
          >
            Sau
          </button>
        </div>
      </div>

      <IpWhitelistModal
        isOpen={modalState.isOpen}
        onClose={() => setModalState({ isOpen: false, ipData: null })}
        onSave={handleSave}
        ipData={modalState.ipData}
      />
    </>
  );
};

export default IpWhitelistPage;
